import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { withAttribution } from "@/lib/outbound";

const STORAGE_KEY = "introBannerDismissed";

const AnnouncementBanner = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();
  const hidden = pathname.startsWith("/media") || pathname.startsWith("/player");

  useEffect(() => {
    setVisible(localStorage.getItem(STORAGE_KEY) !== "true");
  }, []);

  useEffect(() => {
    if (!visible || hidden) {
      document.documentElement.style.setProperty("--banner-h", "0px");
      return;
    }
    const el = ref.current;
    if (!el) return;
    // ResizeObserver reports the banner height post-layout, so we set the
    // --banner-h offset var without a synchronous offsetHeight read during
    // hydration (avoids the forced reflow). Handles resize too. Visually identical.
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.borderBoxSize?.[0]?.blockSize ?? el.offsetHeight;
      document.documentElement.style.setProperty("--banner-h", `${h}px`);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty("--banner-h", "0px");
    };
  }, [visible, hidden]);

  if (!visible || hidden) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
  };

  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center text-xs sm:text-sm font-sweet px-10 py-2 text-center"
      style={{
        backgroundColor: "hsl(var(--velto-beige))",
        color: "hsl(var(--velto-forest))",
      }}
      role="region"
      aria-label="Promotion"
    >
      <p className="leading-snug">
        <span className="font-sweet-bold">Use code INTRO</span> for $5 off an intro class.{" "}
        <a
          href={withAttribution("https://velto.podplay.app/community/events?tag=Intro")}
          target="_blank"
          rel="noopener noreferrer"
          className="underline font-sweet-bold hover:opacity-80 whitespace-nowrap"
        >
          Sign up →
        </a>
      </p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1 hover:opacity-70"
        style={{ color: "hsl(var(--velto-forest))" }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default AnnouncementBanner;